import React, { memo, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAccessFlags, useCompanies, useUser, useProgressiveAuth } from "@/hooks/auth";
import { InlineLoader } from '@/components/ui/progressive-loading';
import { 
  CompanyWorkspaceRoutes, 
  RecruiterWorkspaceRoutes, 
  TalentWorkspaceRoutes, 
  InternalWorkspaceRoutes,
  NannyWorkspaceRoutes,
  MotherWorkspaceRoutes
} from './OptimizedRoutes';
import { CompanySelectionGuard } from '@/components/routing/CompanySelectionGuard';

/**
 * Default redirect component for /workspace/ route
 * Ensures a company is selected and redirects to appropriate workspace
 */
const WorkspaceDefaultRedirect = () => {
  const { 
    hasInternalAccess, 
    hasCompanyAccess, 
    hasRecruiterAccess, 
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess,
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    isFullyLoaded
  } = useProgressiveAuth();

  useEffect(() => {
    // Auto-select company if user has company access but no company selected
    if (hasCompanyAccess && !selectedCompanyId && companies.length > 0) {
      console.log('[WorkspaceDefaultRedirect] Auto-selecting first company:', companies[0].id);
      setSelectedCompanyId(companies[0].id);
    }
  }, [hasCompanyAccess, selectedCompanyId, companies, setSelectedCompanyId]);

  // Wait for auth to fully load
  if (!isFullyLoaded) {
    return <InlineLoader />;
  }

  // Log when users land on /workspace/ without a subpath
  console.warn('[WorkspaceDefaultRedirect] User landed on /workspace/ without subpath', {
    hasCompanyAccess,
    selectedCompanyId,
    companiesCount: companies.length,
    hasInternalAccess,
    hasRecruiterAccess,
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess
  });

  // Check stored workspace preference first (respects user's last used platform)
  const storedMode = localStorage.getItem('workspace_mode');
  if (storedMode === 'nanny' && hasNannyAccess) {
    return <Navigate to="/workspace/nanny/dashboard" replace />;
  }
  if (storedMode === 'mother' && hasMotherAccess) {
    return <Navigate to="/workspace/mother/dashboard" replace />;
  }

  // Priority: nanny > mother > talent > company > recruiter > internal
  // Nanny/mother first as they are separate platforms
  if (hasNannyAccess) return <Navigate to="/workspace/nanny/dashboard" replace />;
  if (hasMotherAccess) return <Navigate to="/workspace/mother/dashboard" replace />;
  if (hasTalentAccess) return <Navigate to="/workspace/talent/jobs" replace />;
  
  // Only redirect to company if we have a selected company
  if (hasCompanyAccess && companies.length > 0) {
    if (selectedCompanyId) {
      return <Navigate to="/workspace/company/hiring-center" replace />;
    } else {
      console.warn('[WorkspaceDefaultRedirect] Company access but no company selected, falling through');
      // Fall through to other workspaces
    }
  }
  
  if (hasRecruiterAccess) return <Navigate to="/workspace/recruiter/network" replace />;
  if (hasInternalAccess) return <Navigate to="/workspace/internal/dashboard" replace />;
  
  // Fallback to Evee AI if no workspace access
  return <Navigate to="/evee-ai" replace />;
};


export const WorkspaceRoutes = memo(() => {
  const { 
    hasInternalAccess, 
    hasCompanyAccess, 
    hasRecruiterAccess 
  } = useAccessFlags();
  const { isReady } = useUser();
  const { isCompanyReady, selectedCompany, companies, selectedCompanyId } = useCompanies();
  const location = useLocation();

  // Handle root /workspace/ route - redirect to appropriate workspace
  if (location.pathname === '/workspace/' || location.pathname === '/workspace') {
    return <WorkspaceDefaultRedirect />;
  }

  // Memoized current mode detection to prevent unnecessary re-calculations
  // Only handle /workspace/* routes - other routes are handled by App.tsx
  const currentMode = React.useMemo((): 'talent' | 'company' | 'recruiter' | 'internal' | 'nanny' | 'mother' => {
    const path = location.pathname;
    
    // Only process workspace routes
    if (!path.startsWith('/workspace/')) {
      return 'talent'; // Default fallback, but this component shouldn't handle non-workspace routes
    }
    
    if (path.includes('/workspace/internal/')) return 'internal';
    if (path.includes('/workspace/company/')) return 'company';
    if (path.includes('/workspace/recruiter/')) return 'recruiter';
    if (path.includes('/workspace/nanny/')) return 'nanny';
    if (path.includes('/workspace/mother/')) return 'mother';
    return 'talent';
  }, [location.pathname]);


  // Stable company key - prevents unnecessary remounts
  const companyKey = selectedCompanyId || 'no-company-selected';

  // For company mode, safety check: don't render company routes without selected company
  if (currentMode === 'company' && hasCompanyAccess) {
    // If no company selected but user has companies, redirect to talent while auto-selection happens
    if (!selectedCompanyId && companies.length > 0) {
      console.warn('[WorkspaceRoutes] Company mode but no company selected, redirecting to talent');
      return <Navigate to="/workspace/talent/jobs" replace />;
    }
    
    // If no companies at all, redirect to talent/evee-ai
    if (companies.length === 0) {
      console.warn('[WorkspaceRoutes] No companies available, redirecting to talent');
      return <Navigate to="/workspace/talent/jobs" replace />;
    }
    
    // Company is selected or being loaded, let guard handle it
    if (!isCompanyReady) {
      return (
        <CompanySelectionGuard>
          <CompanyWorkspaceRoutes key={companyKey} />
        </CompanySelectionGuard>
      );
    }
  }

  // Render the appropriate workspace routes based on current mode
  // Using stable companyKey prevents unnecessary remounts
  switch (currentMode) {
    case 'internal':
      return <InternalWorkspaceRoutes />;
    case 'company':
      return (
        <CompanySelectionGuard>
          <CompanyWorkspaceRoutes key={companyKey} />
        </CompanySelectionGuard>
      );
    case 'recruiter':
      return <RecruiterWorkspaceRoutes key={companyKey} />;
    case 'nanny':
      return <NannyWorkspaceRoutes />;
    case 'mother':
      return <MotherWorkspaceRoutes />;
    case 'talent':
      return <TalentWorkspaceRoutes />;
    default:
      return <TalentWorkspaceRoutes />;
  }
});